// This file needs to be updated to match the new component props
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, XIcon } from "lucide-react";
import { useState } from "react";
import { type Icd10Code, useSearchIcd10Codes } from "@/clinical-codes/useSearchIcd10Codes";
import { useSearchOpcs4Codes, type OPCS4Code as OPCS4CodeType } from "@/clinical-codes/useSearchOpcs4Codes";
import type { CodeType } from "@/clinical-codes/types/ClinicalCodeTypes";

interface CodeSelectorProps {
  selectedCodeId: string;
  selectedCodeType: CodeType;
  onCodeSelect: (codeId: string, codeType: CodeType) => void;
}

type OPCS4Code = OPCS4CodeType & { type: "OPCS-4" };
type ICD10Code = Icd10Code & { type: "ICD-10" };
type Code = OPCS4Code | ICD10Code;

export const CodeSelector = ({ selectedCodeId, selectedCodeType, onCodeSelect }: CodeSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Always call both hooks to satisfy React rules
  const icd10 = useSearchIcd10Codes(searchQuery);
  const opcs4 = useSearchOpcs4Codes(searchQuery);

  let codes: Code[] = [];
  let isLoading = false;

  if (selectedCodeType === "ICD-10") {
    codes = (icd10.codes || []).map((code) => ({ ...code, type: "ICD-10" }));
    isLoading = icd10.isLoading;
  } else if (selectedCodeType === "OPCS-4") {
    codes = (opcs4.codes || []).map((code) => ({ ...code, type: "OPCS-4" }));
    isLoading = opcs4.isLoading;
  }

  const handleCodeTypeChange = (type: CodeType) => {
    onCodeSelect("", type);
  };

  const handleCodeSelect = (id: string) => {
    onCodeSelect(id, selectedCodeType);
  };

  const filteredCodes = codes
    .filter((code) => code.type === selectedCodeType)
    .filter((code) => {
      if (!searchQuery.trim()) return true;
      const lowerQuery = searchQuery.toLowerCase();
      return code.code.toLowerCase().includes(lowerQuery) || code.description.toLowerCase().includes(lowerQuery);
    });

  return (
    <div className="space-y-4">
      <RadioGroup
        value={selectedCodeType}
        onValueChange={(value) => handleCodeTypeChange(value as CodeType)}
        className="flex space-x-4"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="ICD-10" id="icd10" />
          <Label htmlFor="icd10">ICD-10</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="OPCS-4" id="opcs4" />
          <Label htmlFor="opcs4">OPCS-4</Label>
        </div>
      </RadioGroup>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search codes by code or description..."
          className="pl-8"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1 h-7 w-7 p-0"
            onClick={() => setSearchQuery("")}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-4">Loading codes...</div>
      ) : filteredCodes.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">No codes found.</div>
      ) : (
        <ScrollArea className="h-[150px] border rounded-md">
          <div className="p-2">
            {filteredCodes.map((code) => (
              <Button
                key={code.id}
                variant={selectedCodeId === code.id ? "default" : "ghost"}
                className="w-full justify-start text-left mb-1"
                onClick={() => handleCodeSelect(code.id)}
              >
                <div className="truncate">
                  <span className="font-medium">{code.code}</span> - {code.description}
                </div>
              </Button>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};
